import { tryCatchSync } from '@/try-catch';
import type { useAuth0 } from '@auth0/auth0-react';
import type { InferDataFromTag } from '@tanstack/react-query';
import type { ProjectUserRoles } from '@/features/projects/constants';
import type { onboardingQuery } from '@/features/user/queries';

export const safePostLoginHref = (value?: string) => {
  if (!value) return '/';
  if (value.startsWith('//') || value.startsWith('/\\')) return '/';
  const url = tryCatchSync(() =>
    value.startsWith('/') ? new URL(value, window.location.origin) : new URL(value),
  ).data;
  if (!url || url.origin !== window.location.origin) return '/';
  return `${url.pathname}${url.search}${url.hash}` || '/';
};

type Auth0Error = {
  error?: unknown;
  error_description?: unknown;
  message?: unknown;
  name?: unknown;
  code?: unknown;
};

const credentialText = (value: unknown) =>
  typeof value === 'string' ? value.trim().toLowerCase().replace(/\.$/, '') : '';

export const isWrongCredentialsError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;

  const err = error as Auth0Error;
  if (credentialText(err.error) === 'invalid_user_password') return true;

  return [credentialText(err.error_description), credentialText(err.message)].some(
    (text) =>
      text === 'error' ||
      text.includes('wrong email or password') ||
      text.includes('wrong email or passord') ||
      text.includes('incorrect password') ||
      text.includes('invalid password'),
  );
};

const REAUTHENTICATION_CODES = ['invalid_grant', 'login_required', 'missing_refresh_token'];
const REAUTHENTICATION_TEXTS = [
  'refresh token',
  'refresh_token',
  'invalid_grant',
  'login_required',
  "doesn't exist anymore",
  'does not exist anymore',
];

export const isReauthenticationError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;

  const err = error as Auth0Error;
  const code = typeof err.error === 'string' ? err.error : err.code;
  return (
    (typeof code === 'string' && REAUTHENTICATION_CODES.includes(code)) ||
    err.name === 'MissingRefreshTokenError' ||
    [err.error_description, err.message].some(
      (text) =>
        typeof text === 'string' &&
        REAUTHENTICATION_TEXTS.some((needle) => text.toLowerCase().includes(needle)),
    )
  );
};

export const devUserId = import.meta.env.DEV ? import.meta.env.VITE_DEV_USER_ID : undefined;

let isHandlingAuthFailure = false;

const neverResolves = new Promise<never>(() => {});

export const redirectToSessionEnded = (
  reason: 'inactivity' | 'accessRevoked' | 'invitationExpired' | 'sessionExpired',
) => {
  if (isHandlingAuthFailure) return neverResolves;
  isHandlingAuthFailure = true;

  const params = new URLSearchParams({ reason });
  if (reason === 'inactivity' || reason === 'sessionExpired') {
    params.set('target', window.location.pathname + window.location.search);
  }
  const sessionEndedUrl = `/session-ended?${params}`;
  const logout = auth0Exports.getLogout();

  if (logout) {
    logout(window.location.origin + sessionEndedUrl);
  } else {
    window.location.href = sessionEndedUrl;
  }
  return neverResolves;
};

type Auth0 = ReturnType<typeof useAuth0>;

let auth0IsAuthenticated = false;
let auth0GetAccessTokenSilently: Auth0['getAccessTokenSilently'] | null = null;
let auth0Logout: ((returnTo?: string) => void) | null = null;

let authReadyResolve: () => void;
const authReadyPromise = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

export const auth0Exports = {
  getIsAuthenticated: () => auth0IsAuthenticated,
  setIsAuthenticated: (value: boolean) => (auth0IsAuthenticated = value),
  getAccessTokenSilently: () => auth0GetAccessTokenSilently,
  setGetAccessTokenSilently: (func: Auth0['getAccessTokenSilently']) =>
    (auth0GetAccessTokenSilently = func),
  getLogout: () => auth0Logout,
  setLogout: (func: (returnTo?: string) => void) => (auth0Logout = func),
  waitUntilReady: () => authReadyPromise,
  setReady: () => authReadyResolve(),
};

export const authUser = ({
  userData,
  projectRolesData,
}: InferDataFromTag<unknown, ReturnType<typeof onboardingQuery>['queryKey']>) => ({
  ...userData,
  organizationId: userData.organizationId ?? '',
  projectRoleIds: projectRolesData?.roles.reduce(
    (acc, curr) => {
      acc[curr.roleName as ProjectUserRoles] = curr.roleId;
      return acc;
    },
    {} as Record<ProjectUserRoles, string>,
  ),
  organizationRole: userData.organizations.find(
    (org) => org.organizationId === userData.organizationId,
  )?.role,
});
